import { useNotificationSettingsSetQuietHours } from "@sportsmet/notification-sdk/private";
import { Button, Card, SavedNote, Select, Switch } from "@sportsmet/ui";
import { type ReactNode, useState } from "react";

import { showApiError } from "../../errors/apiErrorBus";
import { resolveViewerTimeZone } from "../scores/viewerTimeZone";
import {
  buildQuietHoursTimeOptions,
  parseQuietHoursOptionValue,
} from "./quietHoursOptions";

export const QUIET_HOURS_CARD_TEST_ID = "quiet-hours-card";
export const QUIET_HOURS_ZONE_TEST_ID = "quiet-hours-zone";
export const QUIET_HOURS_ZONE_MISMATCH_TEST_ID = "quiet-hours-zone-mismatch";

export type typeQuietHoursCardProps = {
  isEnabled: boolean;
  startMinute: number;
  endMinute: number;
  /** The zone the server holds for this window ("UTC" when never saved). Never shown as the fan's current zone. */
  savedTimeZone: string;
  onSaved: () => void;
};

export const QuietHoursCard = (props: typeQuietHoursCardProps): ReactNode => {
  const [saved, setSaved] = useState(false);

  const save = useNotificationSettingsSetQuietHours({
    onSuccess: (result) => {
      if (!showApiError(result)) setSaved(true);
      props.onSaved();
    },
  });

  const deviceTimeZone = resolveViewerTimeZone();
  const isSavedInOtherZone =
    props.isEnabled && props.savedTimeZone !== deviceTimeZone;

  const submit = ({
    isEnabled,
    startMinute,
    endMinute,
  }: {
    isEnabled: boolean;
    startMinute: number;
    endMinute: number;
  }): void => {
    setSaved(false);
    save.mutate({
      isEnabled,
      startMinute,
      endMinute,
      timeZone: deviceTimeZone,
    });
  };

  return (
    <Card>
      <div
        data-testid={QUIET_HOURS_CARD_TEST_ID}
        className="flex flex-col gap-4"
      >
        <h2 className="text-h2 text-ink">Quiet hours</h2>
        <Switch
          label="Turn on quiet hours"
          description="Push notifications stay silent during this window. Deadline and lock alerts still come through, and everything still lands in your notifications list."
          checked={props.isEnabled}
          disabled={save.isPending}
          onCheckedChange={(next) => {
            submit({
              isEnabled: next,
              startMinute: props.startMinute,
              endMinute: props.endMinute,
            });
          }}
        />
        <div className="flex flex-col gap-4 sm:flex-row">
          <Select
            label="Quiet from"
            items={buildQuietHoursTimeOptions()}
            value={String(props.startMinute)}
            disabled={save.isPending || !props.isEnabled}
            onValueChange={(value) => {
              const minute = parseQuietHoursOptionValue({ value });
              if (minute === null || minute === props.endMinute) return;
              submit({
                isEnabled: props.isEnabled,
                startMinute: minute,
                endMinute: props.endMinute,
              });
            }}
          />
          <Select
            label="Quiet until"
            items={buildQuietHoursTimeOptions()}
            value={String(props.endMinute)}
            disabled={save.isPending || !props.isEnabled}
            onValueChange={(value) => {
              const minute = parseQuietHoursOptionValue({ value });
              if (minute === null || minute === props.startMinute) return;
              submit({
                isEnabled: props.isEnabled,
                startMinute: props.startMinute,
                endMinute: minute,
              });
            }}
          />
        </div>
        <p
          data-testid={QUIET_HOURS_ZONE_TEST_ID}
          className="text-caption text-ink-secondary"
        >
          Times are in your current time zone, {deviceTimeZone}.
        </p>
        {isSavedInOtherZone ? (
          <div
            data-testid={QUIET_HOURS_ZONE_MISMATCH_TEST_ID}
            className="flex flex-col items-start gap-2"
          >
            <p className="text-caption text-ink-secondary">
              This window still runs on {props.savedTimeZone} time, the zone it
              was saved in.
            </p>
            <Button
              variant="secondary"
              size="sm"
              disabled={save.isPending}
              onClick={() => {
                submit({
                  isEnabled: props.isEnabled,
                  startMinute: props.startMinute,
                  endMinute: props.endMinute,
                });
              }}
            >
              Switch to {deviceTimeZone}
            </Button>
          </div>
        ) : null}
        <SavedNote saved={saved} />
      </div>
    </Card>
  );
};
